import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { useLanguage, CONTACTS } from "../i18n";
import { SHOW_PHOTOS } from "../data";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["storia", t.nav.story],
    ["cucina", t.nav.philosophy],
    ["menu", t.nav.menu],
    ...(SHOW_PHOTOS ? [["galleria", t.nav.gallery]] : []),
    ["spazi", t.nav.spaces],
    ["contatti", t.nav.contacts],
  ];

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
          scrolled ? "shadow-[0_1px_0_rgba(198,146,85,0.18)]" : ""
        }`}
        style={{ backgroundColor: "#480A1A" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo-home"
            onClick={() => scrollTo("top")}
            className="h-9 sm:h-11 w-32 sm:w-40 overflow-hidden shrink-0"
            aria-label="Sant'Irene — home"
          >
            <img
              src="/logo-wordmark.png"
              alt="Sant'Irene"
              className="h-full w-full object-contain"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map(([id, label]) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => scrollTo(id)}
                className="font-meta text-[11px] uppercase tracking-[0.22em] text-[#E9DCCF]/80 hover:text-[#C69255] transition-colors duration-300"
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-4">
            <div
              data-testid="lang-switcher"
              className="flex items-center border border-[#C69255]/35 rounded-full overflow-hidden"
            >
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-switch-${l}-btn`}
                  onClick={() => setLang(l)}
                  className={`px-2.5 sm:px-3 py-1.5 font-meta text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                    lang === l
                      ? "bg-[#C69255] text-[#2C0F1A]"
                      : "text-[#E9DCCF]/70 hover:text-[#C69255]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <a
              data-testid="nav-prenota-btn"
              href={CONTACTS.superbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full border border-[#C69255] text-[#C69255] font-meta text-[11px] uppercase tracking-[0.2em] hover:bg-[#C69255] hover:text-[#2C0F1A] transition-colors duration-300"
            >
              {t.nav.reserve}
            </a>

            <button
              data-testid="mobile-menu-toggle-btn"
              onClick={() => setOpen(true)}
              className="lg:hidden text-[#E9DCCF] p-1.5"
              aria-label="Menu"
            >
              <MenuIcon size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col"
            style={{ backgroundColor: "#480A1A" }}
          >
            <div className="h-16 px-5 flex items-center justify-between">
              <div className="h-9 w-32 overflow-hidden">
                <img src="/logo-wordmark.png" alt="Sant'Irene" className="h-full w-full object-contain" />
              </div>
              <button
                data-testid="mobile-menu-close-btn"
                onClick={() => setOpen(false)}
                className="text-[#E9DCCF] p-1.5"
                aria-label="Chiudi"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {links.map(([id, label], i) => (
                <motion.button
                  key={id}
                  data-testid={`mobile-nav-link-${id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => scrollTo(id), 250);
                  }}
                  className="text-left font-display text-4xl text-[#F5EFE6] py-3 border-b border-[#C69255]/12 hover:text-[#C69255] transition-colors"
                >
                  {label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <a
                data-testid="mobile-menu-prenota-btn"
                href={CONTACTS.superbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center w-full py-4 rounded-full bg-[#C69255] text-[#2C0F1A] font-meta text-xs uppercase tracking-[0.25em]"
              >
                {t.nav.reserve}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
